import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateFalImageForWord } from '../generationsApi';
import { genDebug } from '../debug/generationsDebug';

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

const pickDifferentWord = (pool, exclude) => {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  if (!exclude) return pickRandom(pool);
  if (pool.length === 1) return pool[0];

  // Try a few random picks first (cheap), then fallback to a filtered pool.
  for (let i = 0; i < 6; i++) {
    const w = pickRandom(pool);
    if (w !== exclude) return w;
  }
  const filtered = pool.filter((w) => w !== exclude);
  return filtered.length ? pickRandom(filtered) : pool[0];
};

/**
 * One-item background buffer for Generations rounds.
 *
 * Important: `generateFalImageForWord` charges coins server-side, so prefetching means
 * you're effectively paying for the *next* round while playing the current one.
 *
 * @param initialBuffer - Optional restored buffer from DB (survives app backgrounding).
 */
export const useGenerationsRoundBuffer = ({
  enabled,
  solutionPool,
  excludeWord,
  initialBuffer = null,
  onPrefetchStart,
  onPrefetchEnd,
  onPrefetchError,
  onBufferChange,
  // refreshCoins no longer needed — prefetch doesn't charge coins.
}) => {
  const [bufferedRound, setBufferedRoundInternal] = useState(initialBuffer); // { word, imageUrl }
  const [isPrefetching, setIsPrefetching] = useState(false);
  const inFlightRef = useRef(null);
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);

  // Sync initialBuffer on first meaningful value (after restore from DB).
  useEffect(() => {
    if (initializedRef.current) return;
    if (initialBuffer?.word && initialBuffer?.imageUrl) {
      setBufferedRoundInternal(initialBuffer);
      initializedRef.current = true;
    }
  }, [initialBuffer]);

  // Wrapper to notify parent when buffer changes (for persistence).
  const setBufferedRound = useCallback(
    (round) => {
      setBufferedRoundInternal(round);
      onBufferChange?.(round);
    },
    [onBufferChange]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const canPrefetch = useMemo(() => enabled && Array.isArray(solutionPool) && solutionPool.length > 0, [
    enabled,
    solutionPool,
  ]);

  const clearBuffer = useCallback(() => {
    setBufferedRound(null);
  }, [setBufferedRound]);

  const consumeBufferedRound = useCallback(() => {
    if (!bufferedRound) return null;
    const round = bufferedRound;
    setBufferedRound(null);
    return round;
  }, [bufferedRound, setBufferedRound]);

  const prefetchNext = useCallback(async () => {
    genDebug('prefetchNext:called', {
      enabled,
      hasPool: Array.isArray(solutionPool),
      poolSize: Array.isArray(solutionPool) ? solutionPool.length : 0,
      excludeWord,
      hasBufferedRound: Boolean(bufferedRound),
      isPrefetching,
      hasInFlight: Boolean(inFlightRef.current),
    });

    if (!canPrefetch) return null;
    if (bufferedRound) return bufferedRound;
    // Critical: avoid double-charging coins by starting multiple generations in parallel.
    // React state updates (setIsPrefetching) are async, so two rapid calls could both see
    // `isPrefetching === false` and trigger two requests. Use an in-flight promise ref
    // as the real concurrency lock.
    if (inFlightRef.current) {
      genDebug('prefetchNext:reuse_in_flight');
      return await inFlightRef.current;
    }
    if (isPrefetching) return null;

    const word = pickDifferentWord(solutionPool, excludeWord);
    if (!word) return null;

    genDebug('prefetchNext:start_generation', { word });
    setIsPrefetching(true);
    onPrefetchStart?.(word);

    const p = (async () => {
      try {
        const data = await generateFalImageForWord({ word, prefetch: true });
        if (!mountedRef.current) return null;
        const round = { word: data.word, imageUrl: data.imageUrl };
        setBufferedRound(round);
        genDebug('prefetchNext:generation_success', { word: round.word });
        // No refreshCoins here — prefetch doesn't charge coins. Coins are charged
        // when the buffered round is consumed (Next Round or Skip).
        return round;
      } catch (error) {
        if (!mountedRef.current) return null;
        // If we're out of coins, just keep buffer empty and let UI disable skip/next.
        genDebug('prefetchNext:generation_error', {
          message: error?.message,
          code: error?.code,
          status: error?.status,
        });
        onPrefetchError?.(error);
        return null;
      } finally {
        if (mountedRef.current) {
          setIsPrefetching(false);
          onPrefetchEnd?.();
        }
        inFlightRef.current = null;
        genDebug('prefetchNext:done');
      }
    })();

    inFlightRef.current = p;
    return await p;
  }, [
    bufferedRound,
    canPrefetch,
    excludeWord,
    isPrefetching,
    onPrefetchEnd,
    onPrefetchError,
    onPrefetchStart,
    setBufferedRound,
    solutionPool,
    enabled,
  ]);

  return {
    bufferedRound,
    isPrefetching,
    prefetchNext,
    consumeBufferedRound,
    clearBuffer,
  };
};

